import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import Header from './common/Header';
import Footer from './common/Footer';
import config from '../config';
import { localData, notify, postData } from '../functions';


const Profile = () => {
  const userInfo = localData('user');
  const [passwords, setPasswords] = useState({
    password: '',
    confirm_password: '',
    matching: true,
    confirmTouched: false,
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    if (name === 'password') {
      setPasswords({
        ...passwords,
        [name]: value,
      });
    } else if (name === 'confirm_password') {
      const passwordsMatch = passwords.password === value;
      setPasswords({
        ...passwords,
        [name]: value,
        matching: passwordsMatch && passwords.confirmTouched,
      });
    } else {
      setPasswords({
        ...passwords,
        [name]: value,
      });
    }
  
    if (name === 'confirm_password' && !passwords.confirmTouched) {
      setPasswords({
        ...passwords,
        confirmTouched: true,
      });
    }
  };
  
  const passwordsMatch = passwords.password === passwords.confirm_password;
  
  const passwordClasses = `block outline-0 w-full rounded-md border-0 p-1.5 px-3 shadow-sm ring-1 ring-inset ${
    passwords.matching || (!passwords.password && !passwords.confirm_password)
      ? 'ring-gray-300 focus:ring-indigo-500 dark:border-gray-50 dark:placeholder-gray-400 dark:focus:ring-indigo-500 dark:focus:border-indigo-500'
      : 'ring-red-500'
  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`;


  const handleUpdateProfile = async (event) => {
    event.preventDefault();

    const formData = new FormData(event.target);
    const formValues = Object.fromEntries(formData.entries());

    try {
        const result = await postData(`${config.chartServerUrl}/update/user`, true, formValues);
    
        console.log(result);
        if(result.status === 'success'){
          if(result?.user_data){
            localStorage.setItem('user', JSON.stringify(result.user_data));
            notify('success', result.message);
          }
        } else {
            notify('error', result.message);
        }
    } catch (error) {
        notify('error', 'Something went wrong');
    }
  }

  return (
    <div>
      <Helmet>
        <title>Profile | Medical Chart Management</title>
        <meta name="description" content="Profile Page Description" />
      </Helmet>   
      
      <Header />

      <div className='container mx-auto mt-7 pb-28 px-2'>
        <div className="relative isolate" style={{ 'minHeight': '71vh' }}>

          <div className='flex items-center justify-between'>
            <h4 className='text-left uppercase font-bold text-xl text-gray-900 dark:text-gray-200'>Profile</h4>
            <div className='flex items-center'>
              <Link to="/waiting-room" className='mr-2 font-semibold text-gray-900 dark:text-gray-200'>Chart</Link>
              <span className='text-xs'><ChevronRightIcon className="h-3 w-3 text-gray-500 dark:text-gray-200" /></span>
              <p className='ms-2 text-sm text-gray-600 dark:text-gray-300'>Profile</p>
            </div>
          </div>

          <div className='mt-7 md:mt-12 max-w-6xl mx-auto'>
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
              <div className="rounded-md dark:bg-gray-800 px-8 py-10 border border-gray-200 dark:border-gray-700 shadow">
                <img className="mx-auto h-24 w-24 rounded-full" src={config.appUrl+'/images/user/user.jpg'} alt="User" />
                <h3 className="text-center mt-6 text-lg font-semibold leading-7 tracking-tight text-gray-900 dark:text-gray-200">{userInfo? userInfo.name : ''}</h3>
                <p className="text-center text-sm leading-6 text-gray-400 dark:text-gray-400">{userInfo ? userInfo.email : ''}</p>
                <p className="text-center text-sm leading-6 font-semibold text-indigo-500 dark:text-indigo-400">{userInfo ? userInfo.role_name : ''}</p>
              </div>

              <form autoComplete='off' onSubmit={handleUpdateProfile} className='border border-gray-200 rounded-md shadow dark:bg-gray-800 dark:border-gray-700 md:col-span-2'>
                <div className="px-4 pb-4 pt-4 sm:p-6 sm:pb-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <input type='hidden' name='user_id' value={userInfo? userInfo.user_id : ''} />
                    <div className='col-span-2 sm:col-span-1'>
                      <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Name <span className='text-red-500'>*</span></label>
                      <input required type="text" name="name" placeholder="Full Name" defaultValue={userInfo? userInfo.name : ''}
                        className="block outline-0 w-full rounded-md border-0 p-1.5 px-3 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-indigo-600"
                      />
                    </div>
                    <div className='col-span-2 sm:col-span-1'>
                      <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Email </label>
                      <input required type="text" name="email" placeholder="Email" readOnly defaultValue={userInfo ? userInfo.email : ''}
                        className="block outline-0 w-full rounded-md border-0 p-1.5 px-3 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-indigo-600"
                      />
                    </div>
                    <div className='col-span-2 sm:col-span-1'>
                      <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Password </label>
                      <input type="password" name="password" placeholder="Password" minLength={8} onChange={handleInputChange}
                        className={`${passwords.matching ? 'ring-gray-300 focus:ring-indigo-500' : ''} ${passwordClasses} dark:bg-white/5 dark:text-white`}
                      />
                    </div>
                    <div className='col-span-2 sm:col-span-1'>
                      <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Confirm Password </label>
                      <input type="password" name="confirm_password" placeholder="Confirm Password" minLength={8} onChange={handleInputChange}
                        className={`${passwords.matching ? 'ring-gray-300 focus:ring-indigo-500' : ''} ${passwordClasses} dark:bg-white/5 dark:text-white`}
                      />
                    </div>
                    {!passwordsMatch && passwords.confirmTouched && (
                      <div className='col-span-2 -mt-3'>
                        <p className="text-red-500 text-sm">Passwords do not match.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-4 py-3 text-center sm:px-6">
                  <button type="submit" disabled={!passwordsMatch}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2 text-md mb-3 font-semibold text-white shadow-sm hover:bg-indigo-500 sm:w-auto"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
          
        </div>
      </div>
      
      <Footer />
      
    </div>
  );
};

export default Profile;
